import React from 'react';
import {
  Badge, Button, EmptyState, IconButton, SectionCard, Table, useToast, phone,
} from '@parsina/framework';
import { Copy, History, Inbox, Receipt, ShieldCheck } from 'lucide-react';

import { useTranslation, LTR_TEXT, MIRROR_ICON } from '../../i18n';
import { EVENT_PAGE_SIZE } from '../../constants/list';
import { statusLabelKey } from '../../constants/subscription';
import {
  currencyLabel, eventTypeLabel, moneyUnit, originLabel, periodSourceLabel, planLabel, validUntil,
} from '../../lib/format';
import { SubscriptionBadge, TierBadge } from '../../components/SubscriptionBadge';
import ActorLabel from '../../components/ActorLabel';

/**
 * One labelled fact in the record's definition list.
 *
 * `ltr` marks a value that is an IDENTIFIER rather than prose — a version number,
 * an id — so it reads left-to-right and is isolated from its Persian neighbours.
 */
export function Field({ label, value, ltr, testId }) {
  return (
    <div className="min-w-0" data-testid={testId}>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd
        className={`truncate font-semibold text-foreground ${ltr ? 'font-mono text-[13px]' : ''}`}
        {...(ltr ? LTR_TEXT : { dir: 'auto' })}
      >
        {value}
      </dd>
    </div>
  );
}

/**
 * The subscription's own facts.
 *
 * `graceDays` and `version` are here because they were fetched and shown nowhere:
 * the grace window is the difference between «renew today» and «renew this week»,
 * and the version is what a concurrent-edit conflict is reported against.
 */
export function SubscriptionSection({ clinic, subscription }) {
  const { t, formatDate } = useTranslation();
  const titleId = 'clinic-subscription-title';

  return (
    <SectionCard
      id="subscription"
      title={<span id={titleId}>{t('clinic.sections.subscription')}</span>}
      icon={ShieldCheck}
    >
      {!clinic.hasSubscription ? (
        // Not an error and not an empty state: this is exactly the page an
        // operator opens in order to fix it, so the card explains the situation
        // and the header's primary action does the work.
        <p className="text-sm leading-6 text-muted-foreground">
          {t('clinic.alerts.unprovisioned')}
        </p>
      ) : (
        <dl className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4" aria-labelledby={titleId}>
          <Field label={t('clinic.fields.plan')} value={<TierBadge clinic={clinic} />} />
          <Field
            label={t('clinic.fields.origin')}
            value={originLabel(subscription?.origin, t)}
          />
          <Field
            label={t('clinic.fields.start')}
            value={subscription?.currentPeriodStart
              ? formatDate(subscription.currentPeriodStart)
              : t('common.value.none')}
          />
          <Field
            label={t('clinic.fields.until')}
            value={validUntil(subscription, { t, formatDate })}
          />
          <Field
            label={t('clinic.fields.trialEnd')}
            value={subscription?.trialEndsAt ? formatDate(subscription.trialEndsAt) : t('common.value.none')}
          />
          <Field
            label={t('clinic.fields.grace')}
            value={subscription?.graceEndsAt
              ? `${formatDate(subscription.graceEndsAt)} (${t('clinic.fields.graceDays', { count: subscription.graceDays })})`
              : t('clinic.fields.graceDays', { count: subscription?.graceDays || 0 })}
          />
          <Field
            label={t('clinic.fields.created')}
            value={subscription?.createdAt ? formatDate(subscription.createdAt) : t('common.value.none')}
          />
          <Field label={t('clinic.fields.version')} value={subscription?.version ?? '—'} ltr />
        </dl>
      )}
    </SectionCard>
  );
}

/**
 * What the clinic has paid, and who recorded it.
 *
 * `note` is a column because it is the operator-written justification for a
 * manual period — the thing that makes the row make sense a year later — and it
 * was fetched and dropped. Truncated with a `title`, because a note is a sentence
 * and rows do not wrap (§5.1).
 */
export function PeriodsSection({ periods }) {
  const { t, locale, formatDate, formatMoney } = useTranslation();

  const rows = [...(periods || [])].reverse();   // newest first

  // Each amount is in its own period's currency (PM-0211): IRR on Parsina, the
  // deployment's platform currency elsewhere. Rows that share one currency name
  // it in the header, exactly as the toman header always did; a table that mixes
  // them (periods recorded before a deployment's catalogue changed) names every
  // currency in the header and puts a unit on each figure, so a dollar figure is
  // never read under «تومان».
  const codes = [...new Set(rows.map((p) => p.currency || 'IRR'))];
  const mixed = codes.length > 1;
  // The header NAMES the currency («Montant (toman)»): IRR reads
  // `common.units.tomanName`, not the after-amount word (PM-0185).
  const amountUnit = codes.length
    ? codes.map((code) => (code === 'IRR'
      ? t('common.units.tomanName')
      : moneyUnit(code, { t, locale }))).join(' / ')
    : t('common.units.tomanName');

  return (
    <SectionCard id="periods" title={t('clinic.sections.periods')} icon={Receipt}>
      {rows.length ? (
        <Table
          dense
          ariaLabel={t('clinic.periods.aria')}
          rowKey={(p) => p.id}
          rows={rows}
          columns={[
            { key: 'periodStart', header: t('clinic.periods.columns.start'), render: (p) => formatDate(p.periodStart) },
            { key: 'periodEnd', header: t('clinic.periods.columns.end'), render: (p) => formatDate(p.periodEnd) },
            {
              key: 'priceRial',
              // The header carries the unit, so the cell is the bare figure —
              // and `formatMoney` still does the rial→toman conversion, so there
              // is no second division anywhere in the app.
              header: t('clinic.periods.columns.amount', { unit: amountUnit }),
              align: 'end',
              render: (p) => (
                <span className="tabular-nums">
                  {formatMoney(p.priceRial, {
                    currency: p.currency,
                    withUnit: mixed,
                    unitLabel: t('common.units.toman'),
                    unitStyle: 'short',
                  })}
                </span>
              ),
            },
            {
              key: 'source',
              header: t('clinic.periods.columns.source'),
              render: (p) => <Badge variant="outline">{periodSourceLabel(p.source, t)}</Badge>,
            },
            {
              key: 'activatedBy',
              header: t('clinic.periods.columns.by'),
              render: (p) => <ActorLabel actor={p.activatedBy} />,
            },
            {
              key: 'activatedAt',
              header: t('clinic.periods.columns.at'),
              render: (p) => (p.activatedAt ? formatDate(p.activatedAt) : t('common.value.none')),
            },
            {
              key: 'note',
              header: t('clinic.periods.columns.note'),
              render: (p) => (
                <span dir="auto" className="block max-w-[14rem] truncate" title={p.note || ''}>
                  {p.note || t('common.value.none')}
                </span>
              ),
            },
          ]}
        />
      ) : (
        <EmptyState
          icon={<Inbox size={40} strokeWidth={1.5} aria-hidden />}
          title={t('clinic.periods.empty.title')}
          description={t('clinic.periods.empty.desc')}
        />
      )}
    </SectionCard>
  );
}

/**
 * State transition, as two badges and a direction-aware arrow.
 *
 * The predecessor wrote a literal «←» between the two labels, which points the
 * wrong way the moment the panel renders LTR — and an arrow is the only thing
 * carrying which state came first. Here the glyph mirrors with `--dir-flip`, it is
 * `aria-hidden`, and the sentence «از X به Y» is what a screen reader gets, so the
 * order does not depend on reading a 14px icon at all.
 */
function StatusTransition({ from, to }) {
  const { t } = useTranslation();
  if (!from && !to) return <span>{t('common.value.none')}</span>;
  // The sentence names the states in WORDS. Interpolating the raw enum would read
  // «از TRIALING به ACTIVE» to a screen reader — the one consumer that cannot see
  // the badges the sentence exists to describe.
  const label = (status) => (status ? t(statusLabelKey(status)) : t('common.value.none'));
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="sr-only">
        {t('clinic.events.transition', { from: label(from), to: label(to) })}
      </span>
      <span aria-hidden className="inline-flex items-center gap-1.5">
        {from ? <SubscriptionBadge status={from} /> : <span>—</span>}
        <ChevronGlyph />
        {to ? <SubscriptionBadge status={to} /> : <span>—</span>}
      </span>
    </span>
  );
}

const ChevronGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    width="13" height="13" aria-hidden style={MIRROR_ICON} className="text-muted-foreground">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

/**
 * Every change of state, with its actor — the audit trail.
 *
 * Paged rather than rendered whole: a clinic that has been through a few renewals
 * and a suspension accumulates dozens of rows, and §9.1 caps unbounded rendering.
 * `payload` is behind a toggle because it is JSON — genuinely useful when
 * something looks wrong, and noise in every other row.
 */
export function EventsSection({ events }) {
  const { t, formatDateTime } = useTranslation();
  const [shown, setShown] = React.useState(EVENT_PAGE_SIZE);
  const [openPayload, setOpenPayload] = React.useState(null);

  const rows = (events || []).slice(0, shown);
  const more = (events || []).length - rows.length;

  return (
    <SectionCard
      id="events"
      title={t('clinic.sections.events')}
      sub={t('clinic.sections.eventsSub')}
      icon={History}
    >
      {rows.length ? (
        <>
          <Table
            dense
            ariaLabel={t('clinic.events.aria')}
            rowKey={(e) => e.id}
            rows={rows}
            data-testid="detail-events"
            columns={[
              {
                key: 'occurredAt',
                header: t('clinic.events.columns.at'),
                render: (e) => formatDateTime(e.occurredAt),
              },
              {
                key: 'eventType',
                header: t('clinic.events.columns.type'),
                // An event type this build has never been taught is echoed as its
                // own raw string in an LTR code span — legible and reportable,
                // rather than swallowed or given a plausible wrong label.
                render: (e) => eventTypeLabel(e.eventType, t) || (
                  <code {...LTR_TEXT} className="text-xs text-muted-foreground">{e.eventType}</code>
                ),
              },
              {
                key: 'toStatus',
                header: t('clinic.events.columns.change'),
                render: (e) => <StatusTransition from={e.fromStatus} to={e.toStatus} />,
              },
              {
                key: 'actor',
                header: t('clinic.events.columns.actor'),
                render: (e) => <ActorLabel actor={e.actor} />,
              },
              {
                key: 'payload',
                header: t('clinic.events.columns.detail'),
                render: (e) => (e.payload ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setOpenPayload(openPayload === e.id ? null : e.id)}
                    aria-expanded={openPayload === e.id}
                    aria-label={t('clinic.events.showPayload')}
                  >
                    {openPayload === e.id ? t('common.actions.hide') : t('common.actions.show')}
                  </Button>
                ) : null),
              },
            ]}
          />

          {/* The expanded payload sits under the table rather than inside a row:
              a <pre> in a cell fights the table's own column widths. */}
          {openPayload ? (
            <pre
              {...LTR_TEXT}
              className="mt-2 max-h-48 overflow-auto rounded-lg bg-muted p-3 text-[11px] leading-5"
            >
              {(() => {
                const found = (events || []).find((e) => e.id === openPayload);
                if (!found?.payload) return '';
                try {
                  return JSON.stringify(JSON.parse(found.payload), null, 2);
                } catch {
                  // Not JSON after all — show it verbatim rather than nothing.
                  return found.payload;
                }
              })()}
            </pre>
          ) : null}

          {more > 0 ? (
            <div className="mt-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShown((n) => n + EVENT_PAGE_SIZE)}
                data-testid="detail-events-more"
              >
                {t('clinic.events.more')}
              </Button>
            </div>
          ) : null}
        </>
      ) : (
        <EmptyState
          icon={<History size={40} strokeWidth={1.5} aria-hidden />}
          title={t('clinic.events.empty.title')}
          description={t('clinic.events.empty.desc')}
        />
      )}
    </SectionCard>
  );
}

/**
 * The rail: the registry's own facts, plus the ops playbook.
 *
 * The playbook is static copy and it earns its place — it is the difference
 * between «renew», «override» and «suspend», which is the one decision this
 * screen exists to support and the one an operator new to the panel gets wrong.
 * It lives beside the actions rather than in a wiki nobody opens mid-ticket.
 */
export function ClinicInfoRail({ clinic }) {
  const { t, formatDate, locale } = useTranslation();
  const toast = useToast();

  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(clinic.id);
      toast.success(t('common.value.copied'));
    } catch {
      // A clipboard the browser refuses is not an error worth a red banner: the
      // id is on screen and selectable.
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <SectionCard title={t('clinic.rail.infoTitle')} icon={ShieldCheck}>
        <dl className="grid grid-cols-2 gap-4 text-sm">
          <Field label={t('clinic.fields.type')} value={clinic.clinicType || t('common.value.none')} />
          {/* National form: stored E.164, read and dialled as «021…». */}
          <Field
            label={t('clinic.fields.phone')}
            value={phone.forDisplay(clinic.officePhone) || t('common.value.none')}
            ltr={!!clinic.officePhone}
          />
          <Field
            label={t('clinic.fields.created')}
            value={clinic.createdAt ? formatDate(clinic.createdAt) : t('common.value.none')}
          />
          <Field
            label={t('clinic.fields.registry')}
            value={clinic.active === false ? t('clinics.inactive') : t('common.value.yes')}
          />
          {/* What the clinic's own money is in — not what it pays Parsina, which
              is always toman. Read-only: a currency changes only through the
              clinic panel, which refuses once money exists. */}
          <Field
            label={t('clinic.fields.currency')}
            value={currencyLabel(clinic.currency, { t, locale })}
            testId="detail-currency"
          />
        </dl>

        <div className="mt-1 flex items-center gap-2 border-t border-border pt-3">
          <span className="text-xs text-muted-foreground">{t('clinic.fields.id')}</span>
          <code {...LTR_TEXT} className="min-w-0 flex-1 truncate text-[11px] text-muted-foreground">
            {clinic.id}
          </code>
          <IconButton
            variant="ghost"
            size={32}
            icon={<Copy size={14} />}
            label={t('clinic.rail.copyId')}
            onClick={copyId}
            data-testid="detail-copy-id"
          />
        </div>
      </SectionCard>

      <SectionCard title={t('clinic.rail.playbookTitle')} icon={History}>
        <ul className="flex flex-col gap-2.5 text-xs leading-6 text-muted-foreground">
          <li>{t('clinic.rail.playbookRenew')}</li>
          <li>{t('clinic.rail.playbookOverride')}</li>
          <li>{t('clinic.rail.playbookSuspend')}</li>
        </ul>
      </SectionCard>
    </div>
  );
}
